import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Train of Thought — composant jeu principal (divided attention routing).
 *
 * Pattern unifié `@labrain/game-shell` (DEC-005) :
 *   - `<GameShell>` orchestre xstate + bus + polish + boucle staircase.
 *   - `<PhaseRenderer>` route intro / demo / staircase / wrap_up / debrief.
 *   - `TrainOfThoughtAdapter` (Promise-based) gère sub-phases internes
 *     (fixation → playing 15s → awaiting_resolution → feedback → iti).
 *   - `<DebriefShell>` fournit le squelette debrief partagé.
 *
 * Voix Vesperin : sober, pas de Disney, pas de promesse multitask magique.
 */
import { useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ActionBarProvider, ActionBarSlot, GameShell, PhaseRenderer, useActionBar, useGameShellFromContext, } from '@labrain/game-shell';
import { Demo } from './demo';
import { createTrainOfThoughtAdapter, makeStimulus, } from './adapter';
import { TrainOfThoughtDebriefView, TrainOfThoughtStaircaseView, } from './staircaseView';
import { paradigmMeta, staircaseConfig } from './params';
function Intro({ onShowDemo, onSkipDemo }) {
    useActionBar({
        primary: (_jsx("button", { type: "button", onClick: onShowDemo, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: "Voir un exemple" })),
        secondary: (_jsx("button", { type: "button", onClick: onSkipDemo, className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: "D\u00E9marrer direct" })),
    });
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s6 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: "Aiguillages" }), _jsx("h1", { className: "font-display text-[28px] font-medium leading-9 tracking-cy-display text-cyan-text", children: "Train of Thought" }), _jsx("p", { className: "max-w-[320px] text-[14.5px] leading-5 text-cyan-text-secondary", children: "Dirige les trains vers la voie de leur couleur." }), _jsx("p", { className: "max-w-[320px] text-[12.5px] leading-5 text-cyan-text-secondary", children: "Touche les aiguillages au bon moment, regarde plusieurs trains en parall\u00E8le." }), _jsx(Link, { to: "/library", className: "text-[12px] text-cyan-text-secondary", children: "\u2190 Retour aux jeux" })] }));
}
function IntroSlot() {
    const shell = useGameShellFromContext();
    return (_jsx(Intro, { onShowDemo: () => shell.startSession(), onSkipDemo: () => shell.skipDemo() }));
}
function DemoSlot() {
    const shell = useGameShellFromContext();
    return (_jsx(Demo, { onComplete: () => shell.markDemoDone(), onSkip: () => shell.markDemoDone() }));
}
function WrapUpSlot() {
    const shell = useGameShellFromContext();
    useMemo(() => {
        try {
            window.localStorage.setItem(`labrain.${paradigmMeta.slug}.lastRun`, JSON.stringify({
                ts: Date.now(),
                threshold: shell.snapshot.staircase.thresholdEstimate,
            }));
        }
        catch {
            /* ignored */
        }
        shell.markWrapUpDone();
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}
export function TrainOfThoughtGame() {
    const navigate = useNavigate();
    const adapterRef = useRef(null);
    if (adapterRef.current === null) {
        adapterRef.current = createTrainOfThoughtAdapter(1);
    }
    const adapter = adapterRef.current;
    const config = useMemo(() => ({
        gameSlug: paradigmMeta.slug,
        staircaseConfig,
        showDemo: true,
        showCalibrationWarmup: false,
        enableHaptics: true,
        enableSound: true,
        enableVisualFeedback: true,
    }), []);
    return (_jsxs(ActionBarProvider, { children: [_jsx(GameShell, { config: config, adapter: adapter, buildStimulus: (engStim) => makeStimulus(engStim.trialIdx, engStim.value, 1, engStim.trialIdx + 1), grader: (_stim, resp) => ({
                    correct: resp.success,
                    reactionTimeMs: resp.reactionTimeMs,
                    rawInput: {
                        trainsRouted: resp.trainsRouted,
                        trainsCorrect: resp.trainsCorrect,
                        collisions: resp.collisions,
                    },
                }), children: _jsx(PhaseRenderer, { intro: _jsx(IntroSlot, {}), demo: _jsx(DemoSlot, {}), staircase: _jsx(TrainOfThoughtStaircaseView, { adapter: adapter, onAbort: () => navigate('/library') }), wrapUp: _jsx(WrapUpSlot, {}), debrief: _jsx(TrainOfThoughtDebriefView, {}), aborted: null }) }), _jsx(ActionBarSlot, {})] }));
}
export default TrainOfThoughtGame;
//# sourceMappingURL=game.js.map